import React, { useState } from "react";
import * as m from "../../lib/model";
import { Card, Tile, Logo } from "../shared";
import { Timeline } from "./Timeline";
import { ClassCard } from "./ClassCard";
import { Row } from "./RoomTabs";
import { ChevronDown, Quote, Users, Target, ArrowRight } from "lucide-react";
import { AskReturnsBlock, LpAdvantage } from "./AskReturns";
import { QsbsBlock, qsbsBump } from "./Qsbs";
import { PlatformBlock, AcqStrategyBlock, PlatformFacts, parsePlatformNote, mergeOwn } from "./PlatformBlock";
import { ValuationBlock } from "./Valuation";
import { StructureHero, RefiBlock } from "./Structure";
import { BackLeverCard } from "../BackLeverCard";
import { PrecedentBlock } from "./Precedent";
import { defaultLevers } from "./deck";
import { MarketHero } from "./MarketHero";

const pct = (n) => (isNaN(n) ? "n/a" : m.fmtPct(n));
const entryMult = (M) => m.entryMultiple(M);

// ---------- Opportunity: the executive-summary screen ----------
export function OpportunityTab({ M, title, platform, market, thesis = {}, ask, onInvest, showInvest, minCommit, classes = [], timeline = [], firm = null, own = null }) {
  const wf = m.lpWaterfall(M), cov = m.coverage(M), dscr = cov.dscr, ex = m.exitReturns(M), pf = m.proForma(M);
  const recap = m.isRecap(M);
  const qsbs = thesis.qsbsEligible !== false;
  const bump = qsbs ? qsbsBump(M) : null;
  const paras = Array.isArray(thesis.summary) && thesis.summary.length
    ? thesis.summary
    : [
      `${title} is a ${M.industry || M.businessType || "small business"}${M.yearEstablished ? ` established in ${M.yearEstablished}` : ""} generating ${m.fmt$(M.ttmRevenue)} of trailing revenue and ${m.fmt$(M.sde)} of adjusted earnings${M.employeeCount ? ` with ${M.employeeCount} employees` : ""}${pf.pf ? `, growing to ${m.fmt$(pf.revenue)} of revenue and ${m.fmt$(pf.sde)} of earnings pro-forma once all ${pf.count} add-ons are closed` : ""}.`,
      `The acquisition is funded with ${m.fmt$(m.loanAmt(M))} of senior debt${M.snAmt > 0 ? `, a ${m.fmt$(M.snAmt)} seller note` : ""} and ${m.fmt$(ex.equityInvested)} of equity. Year-${cov.year} cash flow covers debt service ${m.fmtX(dscr)}${cov.pf ? " once fully deployed" : ""} and the base case returns ${m.fmtX(wf.lpMoic)} / ${pct(wf.lpIrr)} to LPs after a ${M.prefReturn}% preferred return.`,
    ];
  const whyTitles = ["Coverage", "Earnings quality", "Entry discipline", "Structure"];
  const why = Array.isArray(thesis.whyNow) && thesis.whyNow.length ? thesis.whyNow : m.verdictReasons(M).slice(0, 3).map((r, i) => ({ title: whyTitles[i] || "Underwriting", text: r }));
  return (
    <div className="ir-screen" data-testid="ir-opportunity">
      <div className="ir-hero">
        <span className="ir-eyebrow">Confidential · Limited partner overview</span>
        <h1 className="ir-hero-h">{title}</h1>
        <div className="ir-hero-meta">
          {[M.industry || M.businessType, M.address, platform ? `Add-on to ${platform.name}` : null, M.dealType].filter(Boolean).map((x, i) => (
            <span className="ir-chip" key={i}>{x}</span>
          ))}
        </div>
      </div>

      <div className="ir-metric-strip" data-testid="ir-metric-strip">
        {[["The Ask", m.fmt$(ask), "LP equity"],
          ["Entry multiple", m.fmtX(entryMult(M)), m.isRollupScope(M) ? "consolidated · price ÷ EBITDA" : "on adjusted SDE"],
          ["Target exit", M.holdIndefinite ? "Hold" : m.fmtX(M.exitMult), M.holdIndefinite ? "cash-yield strategy" : `year ${M.hold}`],
          ["Deal MOIC", m.fmtX(ex.moic), bump ? `QSBS +${m.fmtX(bump.x)} after tax` : (m.isRollupScope(M) ? "full roll-up" : "full deal")],
          ["Deal IRR", pct(ex.irr), bump && bump.bps > 0 ? `QSBS +${bump.bps} bps after tax` : (m.isRollupScope(M) ? "full roll-up" : "full deal")],
          [`DSCR Yr ${cov.year}`, m.fmtX(dscr), cov.pf ? "fully deployed" : "coverage"]].map(([k, val, sub]) => (
          <div className="ir-metric" key={k}><span>{k}</span><b className="mono">{val}</b><em>{sub}</em></div>
        ))}
      </div>

      <div className="ir-2col eq" style={{ marginTop: 14 }}>
        <div>
          <div className="section-label" style={{ marginTop: 0 }}>The opportunity</div>
          {paras.map((p, i) => <p className="ir-para wide" key={i} data-testid={`ir-opp-para-${i}`}>{p}</p>)}
          <LpAdvantage M={M} text={thesis.lpAdvantage} qsbs={qsbs} />
        </div>
        <Card title="At a glance" testid="ir-at-a-glance">
          <Row l="Industry" v={M.industry || M.businessType || "—"} />
          {M.address && <Row l="Location" v={M.address} />}
          {pf.pf ? <>
            <Row l="Pro-forma revenue" v={`${m.fmt$(pf.revenue)} est.`} />
            <Row l="Pro-forma adj. EBITDA" v={m.fmt$(pf.sde)} />
            <Row l="Platform revenue / EBITDA" v={`${m.fmt$(M.ttmRevenue)} · ${m.fmt$(M.sde)}`} />
            <Row l="Total capital deployed" v={m.fmt$(m.sourcesUses(M).totalUses)} />
            <Row l="Blended entry multiple" v={m.fmtX(entryMult(M))} />
          </> : <>
            <Row l="TTM revenue" v={m.fmt$(M.ttmRevenue)} />
            <Row l="Adjusted SDE / EBITDA" v={m.fmt$(M.sde)} />
            <Row l="Gross margin" v={m.grossMargin(M) ? m.fmtPct(m.grossMargin(M)) : "—"} />
            <Row l="Asking price" v={M.askPrice ? m.fmt$(M.askPrice) : "—"} />
            <Row l="Net acquisition cost" v={M.askPrice ? m.fmt$(m.nac(M)) : "—"} />
          </>}
          <Row l="Senior debt" v={`${m.fmt$(m.loanAmt(M))} @ ${M.rate}%`} />
          <Row l="Hold" v={M.holdIndefinite ? "Indefinite" : `${M.hold} years`} />
          <Row l="Preferred return" v={`${M.prefReturn}%`} />
        </Card>
      </div>

      <PlatformBlock M={M} title={title} block={thesis.thePlatform || {}} platformMode={recap} own={mergeOwn(own, thesis.thePlatform)} />
      <MarketHero market={market || {}} block={thesis.marketBlock || {}} />
      {recap && <AcqStrategyBlock M={M} thesis={thesis} block={thesis.acq || {}} />}
      {recap && <ValuationBlock M={M} block={thesis.val || {}} platformMode />}
      {recap && <PrecedentBlock M={M} market={market} block={thesis.precedent || {}} firm={firm} />}
      {recap && <div className="ir-gap"><StructureHero M={M} ask={ask} block={thesis.struct || {}} firm={firm} qsbs={qsbs} recap /></div>}
      <AskReturnsBlock M={M} ask={ask} minCommit={minCommit} classes={classes} timeline={timeline} showInvest={showInvest} onInvest={onInvest} />
      {qsbs && <QsbsBlock M={M} minCommit={minCommit} note={thesis.qsbsNote} ask={ask} />}

      <div className="section-label ir-gap">Why now</div>
      <div className="ir-why-grid" data-testid="ir-why-now">
        {why.map((w, i) => (
          <div className="ir-why-card" key={i} data-testid={`ir-why-${i}`}>
            <b>{w.title}</b><span>{w.text}</span>
          </div>
        ))}
      </div>

      {showInvest && (
        <div className="ir-next" data-testid="ir-next-steps">
          <div>
            <b>Next steps</b>
            <span>Review the data room, put your questions to us in Q&amp;A, then soft-circle your allocation. Soft commitments are non-binding.</span>
          </div>
          <button className="btn btn-primary" onClick={onInvest} data-testid="ir-opp-invest">Review the ask <ArrowRight size={13} /></button>
        </div>
      )}
    </div>
  );
}

// ---------- Thesis ----------
export { parsePlatformNote };

export function ThesisTab({ M, thesis = {}, platform, own }) {
  const ex = m.exitReturns(M);
  const entry = entryMult(M), exit = m.coerceNum(M.exitMult);
  const lift = entry > 0 ? exit - entry : 0;
  const note = parsePlatformNote(own?.notes || platform?.notes);
  const noteThesis = note.find((n) => /thesis|platform note/i.test(n.title));
  const noteRest = note.filter((n) => n !== noteThesis);
  const levers = Array.isArray(thesis.levers) && thesis.levers.length ? thesis.levers : defaultLevers(M);
  const paths = Array.isArray(thesis.exitPaths) && thesis.exitPaths.length ? thesis.exitPaths : [
    `Sale to a regional consolidator or strategic buyer at ${m.fmtX(exit)} on ${m.fmt$(ex.sdeExit)} of exit earnings.`,
    "Recapitalisation: refinance senior debt to return capital while retaining the platform.",
    M.holdIndefinite ? "Indefinite hold with distributions funded from free cash flow." : null,
  ].filter(Boolean);
  const paras = Array.isArray(thesis.summary) && thesis.summary.length ? thesis.summary
    : noteThesis ? [noteThesis.text]
      : [`We buy essential, cash-generative businesses at conservative multiples and hold them for cash flow. ${M.dealName || "This platform"} fits: ${m.fmt$(M.sde)} of adjusted earnings at ${m.fmtX(entry)} entry, with ${m.fmtX(m.dscrYr1(M))} year-one debt service coverage.`];
  return (
    <div className="ir-screen" data-testid="ir-thesis">
      <div className="ir-2col">
        <div>
          <div className="section-label">Investment thesis</div>
          {paras.map((p, i) => <p className="ir-para" key={i}>{p}</p>)}
          {platform && <div className="ir-callout" data-testid="ir-thesis-platform"><span className="ir-callout-h"><Target size={12} /> Platform fit</span>This acquisition bolts on to {platform.name} ({platform.industry || "platform"}), sharing crews, back office and customer base — earnings arrive at the platform's cost structure rather than standalone.</div>}
        </div>
        <Card title="The arbitrage" testid="ir-arbitrage">
          <div className="ir-arb">
            <div><span>Buy at</span><b className="mono">{m.fmtX(entry)}</b></div>
            <ArrowRight size={16} />
            <div><span>Exit at</span><b className="mono pos">{M.holdIndefinite ? "—" : m.fmtX(exit)}</b></div>
          </div>
          <p className="ir-para sm">{lift > 0 ? `Every incremental $1 of earnings is worth roughly $${lift.toFixed(2)} more at exit than it cost at entry.` : "Underwritten without multiple expansion — returns come from cash flow and debt paydown."}</p>
          <Row l="Exit earnings" v={m.fmt$(ex.sdeExit)} />
          <Row l="Exit enterprise value" v={M.holdIndefinite ? "—" : m.fmt$(ex.exitEV)} />
          <Row l="Equity at exit" v={M.holdIndefinite ? "—" : m.fmt$(ex.equityAtExit)} />
        </Card>
      </div>

      {mergeOwn(own, thesis.thePlatform) && <div className="ir-gap"><PlatformFacts M={M} own={mergeOwn(own, thesis.thePlatform)} /></div>}

      {noteRest.length > 0 && (
        <>
          <div className="section-label ir-gap">The playbook</div>
          <div className="ir-why-grid lg-grid" data-testid="ir-thesis-playbook">
            {noteRest.map((n, i) => <div className="ir-why-card lg" key={i} data-testid={`ir-thesis-note-${i}`}><b>{n.title}</b><span>{n.text}</span></div>)}
          </div>
        </>
      )}

      <div className="section-label ir-gap">Value creation levers</div>
      <ol className="ir-levers" data-testid="ir-levers">
        {levers.map((l, i) => <li key={i} data-testid={`ir-lever-${i}`}><b>{l.title}</b><span>{l.text}</span></li>)}
      </ol>

      <div className="section-label ir-gap">Exit paths</div>
      <div className="ir-paths" data-testid="ir-exit-paths">
        {paths.map((p, i) => <div className="ir-path" key={i}><Quote size={11} />{p}</div>)}
      </div>
    </div>
  );
}

// ---------- Pipeline / targets ----------
const stageTone = (s) => (/closed|loi|under/i.test(s || "") ? "go" : /nda|diligence/i.test(s || "") ? "amber" : "");
const anonName = (t) => [t.industry || "Add-on target", t.location].filter(Boolean).join(" · ");

export function PipelineTab({ M, pipeline = [] }) {
  const [open, setOpen] = useState("");
  const rollup = ((M.rollup && M.rollup.companies) || []).filter((c) => c.name);
  const rows = pipeline.filter((p) => p.include !== false);
  const totalEbitda = rows.reduce((a, r) => a + (+r.ebitda || 0), 0);
  const avgMult = rows.filter((r) => +r.multiple > 0);
  if (!rows.length && !rollup.length) {
    return <Card title="Pipeline"><div className="bullet">No add-on pipeline has been published for this offering yet.</div></Card>;
  }
  return (
    <div className="ir-screen" data-testid="ir-pipeline">
      <div className="grid g4">
        <Tile label="Targets in pipeline" value={rows.length} testid="ir-pipe-count" />
        <Tile label="Aggregate target EBITDA" value={m.fmt$(totalEbitda)} tone="go" />
        <Tile label="Avg. entry multiple" value={avgMult.length ? m.fmtX(avgMult.reduce((a, r) => a + +r.multiple, 0) / avgMult.length) : "—"} />
        <Tile label={m.isRollupScope(M) ? "Consolidated entry" : "Platform entry"} value={m.fmtX(entryMult(M))} sub={m.isRollupScope(M) ? "total price ÷ EBITDA" : "on adjusted SDE"} />
      </div>
      <Card title="Acquisition pipeline" className="table-scroll ir-gap" testid="ir-pipe-table">
        <table className="tbl">
          <thead><tr><th>Target</th><th>Geography</th><th className="num">Revenue</th><th className="num">EBITDA</th><th className="num">Entry</th><th>Stage</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="om-sub">No individual targets published yet — the modelled sequence below shows the planned add-on cadence.</td></tr>}
            {rows.map((t, i) => (
              <React.Fragment key={t.id || i}>
                <tr data-testid={`ir-pipe-row-${i}`}>
                  <td>
                    <button className="om-co" onClick={() => setOpen(open === i ? "" : i)} data-testid={`ir-pipe-expand-${i}`}>
                      <b>{t.reveal ? (t.name || anonName(t)) : anonName(t)}</b><ChevronDown size={12} className={open === i ? "rot" : ""} />
                    </button>
                    {!t.reveal && <div className="om-sub">Identity withheld until NDA</div>}
                  </td>
                  <td>{t.location || "—"}</td>
                  <td className="num">{t.revenue ? m.fmt$(t.revenue) : "—"}</td>
                  <td className="num">{t.ebitda ? m.fmt$(t.ebitda) : "—"}</td>
                  <td className="num">{+t.multiple > 0 ? m.fmtX(+t.multiple) : "—"}</td>
                  <td><span className={`pill ${stageTone(t.stage)}`}>{t.stage || "Sourcing"}</span></td>
                </tr>
                {open === i && (
                  <tr className="om-detail" data-testid={`ir-pipe-detail-${i}`}>
                    <td colSpan={6}>
                      <div className="om-detail-grid">
                        <div>
                          <div className="section-label">Why this target</div>
                          <div className="om-rationale">{t.note || "Fits the add-on criteria on size, geography and service mix; owner-retirement situation with no succession plan."}</div>
                        </div>
                        <div>
                          <div className="section-label">Detail</div>
                          <div className="om-kv">
                            {t.industry && <div><span>Sector</span>{t.industry}</div>}
                            {t.employees && <div><span>Employees</span>{t.employees}</div>}
                            {t.serviceMix && <div><span>Service mix</span>{t.serviceMix}</div>}
                            <div><span>Status</span>{t.stage || "Sourcing"}</div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </Card>
      {rollup.length > 0 && (
        <Card title="Modelled roll-up sequence" className="table-scroll ir-gap" testid="ir-pipe-rollup">
          <table className="tbl">
            <thead><tr><th>Entity</th><th className="num">EBITDA</th><th className="num">Multiple</th><th className="num">Year</th></tr></thead>
            <tbody>{rollup.map((c, i) => (
              <tr key={i}><td>{c.name}</td><td className="num">{m.fmt$(c.ebitda)}</td><td className="num">{m.fmtX(c.multiple)}</td><td className="num">{c.year === 0 ? "Platform" : `Yr ${c.year}`}</td></tr>
            ))}</tbody>
          </table>
        </Card>
      )}
      <div className="ir-foot-note">Pipeline targets are indicative and unsigned; identities are withheld until an NDA is in place. Nothing here is a commitment to transact.</div>
    </div>
  );
}

// ---------- Team ----------
export function TeamTab({ team = [], firm = null, M }) {
  const people = team.filter((p) => (p.name || "").trim());
  const about = (firm && (firm.mission || firm.bio)) || "";
  return (
    <div className="ir-screen" data-testid="ir-team">
      <div className="ir-deck-head" style={{ marginTop: 0 }}>
        <span className="ir-eyebrow">The sponsor &amp; team</span>
        <h2 className="ir-deck-h" data-testid="ir-team-headline">{firm?.name || "XYZ Ventures"}{firm?.tagline ? ` — ${firm.tagline}` : ""}</h2>
      </div>
      <div className="ir-teambook">
        <aside className="ir-teambook-firm" data-testid="ir-team-firm">
          <div className="ir-teambook-logo"><Logo height={120} testid="ir-team-logo" /></div>
          {about.split(/\n+/).filter((x) => x.trim()).map((p, i) => <p className="ir-para" key={i}>{p}</p>)}
          {firm?.founded && <div className="ir-foot-note">Founded {firm.founded}{firm.address ? ` · ${firm.address}` : ""}</div>}
        </aside>
        <div className="ir-teambook-people" data-testid="ir-team-grid">
          {people.length === 0 && <Card title="Team"><div className="bullet">Team profiles have not been published for this offering yet.</div></Card>}
          {people.map((p, i) => (
            <div className="ir-profile-row" key={i} data-testid={`ir-person-${i}`}>
              {p.photo ? <img src={p.photo} alt={p.name} className="ir-profile-photo" /> : <span className="ir-profile-photo ph"><Users size={26} /></span>}
              <div>
                <b className="ir-profile-name">{p.name}<span> / Background</span></b>
                {p.role && <span className="ir-person-role">{p.role}</span>}
                {p.bio && <p className="ir-para sm">{p.bio}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
      {M.analystContact && (
        <div className="ir-callout ir-gap" data-testid="ir-team-contact">
          <span className="ir-callout-h">Deal contact</span>
          {M.analystContact}{M.analystEmail ? ` · ${M.analystEmail}` : ""}{M.analystPhone ? ` · ${M.analystPhone}` : ""}
        </div>
      )}
    </div>
  );
}

// ---------- Structure & terms ----------
export function StructureTab({ M, classes = [], timeline = [], ask, thesis = {}, firm }) {
  const recap = m.isRecap(M), qsbs = thesis.qsbsEligible !== false;
  return (
    <div className="ir-screen" data-testid="ir-structure">
      <StructureHero M={M} ask={ask || m.lpAskOf(M)} block={thesis.struct || {}} firm={firm} qsbs={qsbs} recap={recap} />
      <RefiBlock M={M} ask={ask || m.lpAskOf(M)} block={thesis.refi || {}} qsbs={qsbs} recap={recap} />
      {m.isBackLever(M) && (
        <div className="ir-gap" data-testid="ir-backlever">
          <BackLeverCard M={M} V={M} patch={() => {}} readOnly />
        </div>
      )}
      {classes.length > 0 && <div className="ir-gap"><ClassCard classes={classes} /></div>}
      <Card title="Key dates" className="ir-gap" testid="ir-key-dates"><Timeline items={timeline} testid="ir-structure-timeline" /></Card>
      <div className="ir-foot-note">Terms are indicative and subject to final documentation. Illustrative projections only; not an offer to sell securities.</div>
    </div>
  );
}
